/**
 * transform — teacher-list transformation logic from Angular
 * `DEEComponent.GetAllTeacherList()` / `GetAllTeacherList1()` /
 * `GetAllTeacherList2()`.
 *
 * Resolves subject/designation labels, applies the designation remaps, sorts by
 * seniority, and splits the raw result into teacher / absent / notWilling /
 * final lists with the role/location filtering Angular used.
 */

import { CounsellingRecord, Option, RoleContext } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

/** Resolve a designation code to its label from the staff list. */
export function designationLabel(designationList: any[], value: any): string {
  const found = designationList.filter((ct) => str(ct.value) === str(value));
  return found.length > 0 ? found[0].label : '-';
}

/** Resolve a subject code to its label from the subject list. */
function subjectLabel(subList: Option[], value: any): string {
  const found = subList.filter((ct) => str(ct.value) === str(value));
  return found.length > 0 ? found[0].label : '-';
}

/**
 * Angular remapped a few designation codes before label lookup:
 *   108 → 134, 253 → 146, 252 → 139
 */
function remapDesignation(desg: any): any {
  const d = str(desg);
  if (d === '108') return 134;
  if (d === '253') return 146;
  if (d === '252') return 139;
  return desg;
}

export interface TeacherListsResult {
  teacherList: CounsellingRecord[];
  absentList: CounsellingRecord[];
  notWillingList: CounsellingRecord[];
  finalList: CounsellingRecord[];
  /** Angular: teacherPriority / teacherPriorityId (drive top-row action gating). */
  teacherPriority: any;
  teacherPriorityId: any;
}

interface TransformArgs {
  result: CounsellingRecord[];
  startEndRow: CounsellingRecord;
  role: RoleContext;
  designationList: any[];
  subList: Option[];
  path: string;
  /** true for GetAllTeacherList1 (adds HmAprvSts filter + special_priority). */
  requireApproved?: boolean;
}

/**
 * Build teacher/absent/notWilling/final lists from a teacher-list API result.
 * Covers GetAllTeacherList (requireApproved=false) and GetAllTeacherList1
 * (requireApproved=true).
 */
export function transformTeacherLists({
  result,
  startEndRow,
  role,
  designationList,
  subList,
  path,
  requireApproved = false,
}: TransformArgs): TeacherListsResult {
  const { userTypeId: u, userType1: u1, districtId } = role;
  const location = str(startEndRow.Location);

  // ── Active teacher list ──────────────────────────────────────────────────
  let teacherList = result.filter((ab) => {
    const active =
      (str(ab.teacher_status) === '0' || ab.teacher_status == null) &&
      Number(ab.seniority_state) > 0 &&
      ab.seniority_state != null;
    return requireApproved ? active && ab.HmAprvSts === 'Approved' : active;
  });

  teacherList = teacherList.map((t) => ({
    ...t,
    seniority_state: Number(t.seniority_state),
    minority: t.MinorityType ? t.MinorityType : '-',
  }));

  // Sort ascending by seniority.
  teacherList.sort((a, b) =>
    Number(a.seniority_state) < Number(b.seniority_state) ? -1 : 1
  );

  const teacherPriority =
    location === '3'
      ? teacherList.length > 0
        ? teacherList[0].District
        : result
      : teacherList.length > 0
        ? teacherList[0].uptId
        : result;

  const teacherPriorityId =
    location === '3' && teacherList.length > 0 ? teacherList[0].uptId : '';

  // Resolve labels + designation remaps.
  teacherList = teacherList.map((t) => {
    const desg = remapDesignation(t.desgnation);
    return {
      ...t,
      desgnation: desg,
      subjectLabel: subjectLabel(subList, t.subject),
      desigLabel: designationLabel(designationList, desg),
      special_priority_label:
        str(t.special_priority) === '1' ? 'சிறப்பு முன்னுரிமை' : '-',
    };
  });

  // ── Absent / not willing ───────────────────────────────────────────────────
  const absentList = result.filter((ab) => str(ab.teacher_status) === '2');
  const notWillingList = result.filter((ab) => str(ab.teacher_status) === '3');

  // ── Final list (taken) with location/role filtering ────────────────────────
  let finalList: CounsellingRecord[];
  if (location === '1' && districtId !== 16 && u === '10' && u1 === '2') {
    finalList = result.filter(
      (ab) =>
        str(startEndRow.BlockId) === str(ab.Block) &&
        str(ab.teacher_status) === '1'
    );
  } else if (location === '2') {
    if (u === '6' && u1 === '29') {
      finalList = result.filter((ab) => str(ab.teacher_status) === '1');
    } else {
      finalList = result.filter(
        (ab) =>
          districtId === Number(ab.District) && str(ab.teacher_status) === '1'
      );
    }
  } else if (
    location === '1' &&
    districtId === 16 &&
    u === '10' &&
    u1 === '2'
  ) {
    finalList = result.filter(
      (ab) =>
        districtId === Number(ab.EduDisId) && str(ab.teacher_status) === '1'
    );
  } else {
    finalList = result.filter((ab) => str(ab.teacher_status) === '1');
  }

  // Resolve final-list labels.
  finalList = finalList.map((f) => ({
    ...f,
    Designation: labelOrRaw(designationList, f.desgnation),
    NewDesignation: labelOrRaw(designationList, f.new_designation),
    Subject: labelOrRaw(subList as any, f.subject),
    NewSubject: labelOrRaw(subList as any, f.new_subject),
    pdfLoading: false,
  }));

  // ── path === 'candidate' override (approved-only, seniority sorted) ─────────
  if (path === 'candidate') {
    let candidateList = result.filter((ftr) => ftr.HmAprvSts === 'Approved');
    candidateList = candidateList.map((t) => ({
      ...t,
      seniority_state: Number(t.seniority_state),
    }));
    candidateList.sort((a, b) =>
      Number(a.seniority_state) < Number(b.seniority_state) ? -1 : 1
    );
    candidateList = candidateList.map((t) => ({
      ...t,
      subjectLabel: subjectLabel(subList, t.subject),
    }));
    teacherList = candidateList;
  }

  return {
    teacherList,
    absentList,
    notWillingList,
    finalList,
    teacherPriority,
    teacherPriorityId,
  };
}

/** Returns the resolved label, or the raw value when no match (Angular pattern). */
function labelOrRaw(list: any[], value: any): any {
  const found = list.filter((ct) => str(ct.value) === str(value));
  return found.length > 0 ? found[0].label : value;
}

/* eslint-enable @typescript-eslint/no-explicit-any */
